// ═══════════════════════════════════════
// ATLAS BEASTS — API CLIENT
// Cloudflare Workers backend
// ═══════════════════════════════════════

import type { ApiResponse, Player, Creature, Character, BountyEntry, Trade, Battle } from '../types'
import { getStoredToken } from '../store/auth'

const API_BASE = import.meta.env.DEV
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'https://atlas-beasts-api.ploonu.workers.dev')

// ── Base fetch wrapper ──
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getStoredToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Network error' }))
      return { success: false, error: error.message || `HTTP ${res.status}` }
    }

    const body = await res.json()
    // Worker already returns { success, data, error } — pass through directly
    if (typeof body === 'object' && body !== null && 'success' in body) return body
    return { success: true, data: body }
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}

// ── Player API ──
export const PlayerAPI = {
  getMe: () =>
    apiFetch<Player>('/player/me'),

  getPlayer: (id: string) =>
    apiFetch<Player>(`/player/${id}`),

  updateZone: (zoneId: string) =>
    apiFetch<Player>('/player/zone', {
      method: 'PATCH',
      body: JSON.stringify({ zoneId }),
    }),

  updateFaction: (factionId: string) =>
    apiFetch<Player>('/player/faction', {
      method: 'PATCH',
      body: JSON.stringify({ factionId }),
    }),
}

// ── Creature API ──
export const CreatureAPI = {
  getMyCreatures: () =>
    apiFetch<Creature[]>('/creature/mine'),

  getCreature: (id: string) =>
    apiFetch<Creature>(`/creature/${id}`),

  catchCreature: (bountyId: string) =>
    apiFetch<Creature>('/creature/catch', {
      method: 'POST',
      body: JSON.stringify({ bountyId }),
    }),

  bondCreature: (creatureId: string, characterId: string) =>
    apiFetch<Creature>('/creature/bond', {
      method: 'POST',
      body: JSON.stringify({ creatureId, characterId }),
    }),

  listForTrade: (creatureId: string, askingPrice: number) =>
    apiFetch<Creature>('/creature/list', {
      method: 'POST',
      body: JSON.stringify({ creatureId, askingPrice }),
    }),

  unlistFromTrade: (creatureId: string) =>
    apiFetch<Creature>('/creature/unlist', {
      method: 'POST',
      body: JSON.stringify({ creatureId }),
    }),

  evolve: (creatureId: string) =>
    apiFetch<Creature & { boostedStats: Record<string, number>; soulbound: boolean }>('/creature/evolve', {
      method: 'POST',
      body: JSON.stringify({ creatureId }),
    }),

  getMarketplace: (filters?: { element?: string; rarity?: string; zone?: string }) =>
    apiFetch<Creature[]>('/creature/marketplace' + (filters ? '?' + new URLSearchParams(filters as Record<string,string>).toString() : '')),
}

// ── Character API ──
export const CharacterAPI = {
  getMyCharacters: () =>
    apiFetch<Character[]>('/character/mine'),

  getCharacter: (id: string) =>
    apiFetch<Character>(`/character/${id}`),

  awakening: (characterId: string) =>
    apiFetch<Character>('/character/awaken', {
      method: 'POST',
      body: JSON.stringify({ characterId }),
    }),
}

// ── Bounty Board API ──
export const BountyAPI = {
  getActiveBounties: (zoneId?: string) =>
    apiFetch<BountyEntry[]>('/bounty/active' + (zoneId ? `?zone=${zoneId}` : '')),

  getBounty: (id: string) =>
    apiFetch<BountyEntry>(`/bounty/${id}`),
}

// ── Trade API ──
export const TradeAPI = {
  getMyTrades: () =>
    apiFetch<Trade[]>('/trade/mine'),

  createTrade: (toPlayerId: string, offeredCreatureId: string, requestedCreatureId: string) =>
    apiFetch<Trade>('/trade/create', {
      method: 'POST',
      body: JSON.stringify({ toPlayerId, offeredCreatureId, requestedCreatureId }),
    }),

  acceptTrade: (tradeId: string) =>
    apiFetch<Trade>('/trade/accept', {
      method: 'POST',
      body: JSON.stringify({ tradeId }),
    }),

  rejectTrade: (tradeId: string) =>
    apiFetch<Trade>('/trade/reject', {
      method: 'POST',
      body: JSON.stringify({ tradeId }),
    }),

  cancelTrade: (tradeId: string) =>
    apiFetch<Trade>('/trade/cancel', {
      method: 'POST',
      body: JSON.stringify({ tradeId }),
    }),
}

// ── Battle API ──
export const BattleAPI = {
  reportEncounter: (payload: {
    bountyId: string
    result: 'victory' | 'defeat' | 'fled'
    crewCharacterTemplateId?: string
    turnsUsed?: number
  }) =>
    apiFetch<{ battleId: string; result: string; xpGained: number; coinsGained: number; caughtCreatureId: string | null }>(
      '/battle/encounter',
      { method: 'POST', body: JSON.stringify(payload) }
    ),

  getBattleHistory: () =>
    apiFetch<Battle[]>('/battle/history'),
}

// ── Analytics API ──
export const AnalyticsAPI = {
  track: (event: string, properties?: Record<string, unknown>, playerId?: string) => {
    // Fire-and-forget — never await in hot paths
    apiFetch('/events/track', {
      method: 'POST',
      body: JSON.stringify({ event, properties, playerId }),
    }).catch(() => {})
  },
}

// ── Error API ──
export const ErrorAPI = {
  report: (message: string, stack?: string, playerId?: string) => {
    apiFetch('/errors/report', {
      method: 'POST',
      body: JSON.stringify({
        message,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        playerId,
      }),
    }).catch(() => {})
  },
}

// ── Season API ──
export const SeasonAPI = {
  getCurrent: () =>
    apiFetch<{
      season: { id: string; name: string; number: number; theme_element: string; starts_at: string; ends_at: string; daysRemaining: number; battle_pass_price_shards: number }
      tiers: Array<{ id: string; tier: number; xp_required: number; free_reward_type: string | null; free_reward_value: string | null; premium_reward_type: string | null; premium_reward_value: string | null }>
      playerProgress: { seasonXP: number; currentTier: number; hasPremiumPass: boolean; purchasedAt: string | null }
      nextTierXP: number
      xpToNextTier: number
    }>('/season/current'),

  purchasePass: () =>
    apiFetch<{ player: unknown; unlockedRewards: unknown[] }>('/season/purchase-pass', { method: 'POST' }),

  claimReward: (tierId: string) =>
    apiFetch<{ reward: { tierId: string; tier: number; type: string | null; value: string | null; isPremium: boolean }; player: unknown }>('/season/claim-reward', {
      method: 'POST',
      body: JSON.stringify({ tierId }),
    }),
}

// ── Waitlist API ──
export const WaitlistAPI = {
  join: (email: string) =>
    apiFetch<{ message: string; isFoundingMember: boolean }>('/waitlist/join', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getCount: () =>
    apiFetch<{ count: number }>('/waitlist/count'),
}
